import clsx from 'clsx';
import type { ReactNode } from 'react';
import { formatInt } from '@/lib/format';
import { Card } from '@/components/admin/ui';
import { Sparkline } from './Sparkline';

export type KpiTileProps = {
  label: string;
  value: number | string;
  delta?: number;
  spark?: number[];
  loading?: boolean;
  /** Native browser tooltip on the whole tile (hover to read). */
  tooltip?: string;
  /** Small mark beside the label, such as a village sprite. */
  icon?: ReactNode;
};

function formatDelta(d: number) {
  const pct = (d * 100).toFixed(1);
  const sign = d > 0 ? '+' : '';
  return `${sign}${pct}%`;
}

export function KpiTile({ label, value, delta, spark, loading, tooltip, icon }: KpiTileProps) {
  return (
    <Card
      padding="md"
      className={tooltip ? 'flex cursor-help flex-col justify-between' : 'flex flex-col justify-between'}
      title={tooltip}
    >
      <div>
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-nova-muted-foreground">{label}</p>
          {icon ? <div className="shrink-0">{icon}</div> : null}
        </div>
        {loading ? (
          <div className="mt-2 h-9 w-24 animate-pulse rounded-lg bg-nova-muted" />
        ) : (
          <p className="mt-1 text-xl font-semibold tracking-tight tabular-nums text-nova-foreground">
            {typeof value === 'number' ? formatInt(value) : value}
          </p>
        )}
        {!loading && delta !== undefined && (
          <p
            className={clsx(
              'mt-1 text-xs font-medium',
              delta > 0 && 'text-emerald-600',
              delta < 0 && 'text-red-600',
              delta === 0 && 'text-nova-muted-foreground'
            )}
          >
            {formatDelta(delta)} vs prior period
          </p>
        )}
      </div>
      {!loading && spark && spark.length > 0 && (
        <div className="mt-3">
          <Sparkline data={spark} ariaLabel={`${label} trend`} />
        </div>
      )}
    </Card>
  );
}
